/**
 * RealtimeProvider — wraps the project view with a single WS connection
 * and re-exposes the inbound events as React state + a small imperative API.
 *
 * Scene consumes:
 *   - `members`      — list of other users currently in the room (presence panel)
 *   - `remoteCursors`— map<userId, RemoteCursor> for the 3D cursor manager
 *   - `emitCursor`   — call on every mousemove (throttled here, 100 ms)
 *   - `onAnnotation` — subscribe to create/delete echoes (so we add/remove geometry)
 *   - `status`       — for the "Verbindung wird wiederhergestellt…" badge
 *
 * Subscriptions for annotation events live in a Set so multiple consumers
 * could attach if we ever need that. Today only Scene uses it.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { connect, WsHandle } from './wsClient'
import type {
  PresenceMember,
  RemoteCursor,
  ServerEvent,
  WsStatus,
} from './types'

type AnnotationEvent = Extract<ServerEvent, { type: 'annotation:create' | 'annotation:delete' }>
type ScreenshotEvent = Extract<ServerEvent, { type: 'screenshot:create' | 'screenshot:delete' }>

export interface RealtimeAPI {
  status: WsStatus
  members: PresenceMember[]
  remoteCursors: Map<string, RemoteCursor>
  emitCursor: (p: RemoteCursor['p']) => void
  onAnnotation: (handler: (ev: AnnotationEvent) => void) => () => void
  onScreenshot: (handler: (ev: ScreenshotEvent) => void) => () => void
}

const noopAPI: RealtimeAPI = {
  status: 'closed',
  members: [],
  remoteCursors: new Map(),
  emitCursor: () => undefined,
  onAnnotation: () => () => undefined,
  onScreenshot: () => () => undefined,
}

const Ctx = createContext<RealtimeAPI>(noopAPI)

const CURSOR_THROTTLE_MS = 100

export function useRealtime(): RealtimeAPI {
  return useContext(Ctx)
}

interface ProviderProps {
  projectId: string
  children: React.ReactNode
}

export function RealtimeProvider({ projectId, children }: ProviderProps) {
  const [status, setStatus] = useState<WsStatus>('connecting')
  const [members, setMembers] = useState<PresenceMember[]>([])
  // We mutate the cursor map in place + bump a counter to nudge React
  // (full Map copies on every 10 Hz update would be wasteful).
  const remoteCursorsRef = useRef<Map<string, RemoteCursor>>(new Map())
  const [, forceCursorTick] = useState(0)
  const handleRef = useRef<WsHandle | null>(null)
  const annotationHandlersRef = useRef<Set<(ev: AnnotationEvent) => void>>(new Set())
  const screenshotHandlersRef = useRef<Set<(ev: ScreenshotEvent) => void>>(new Set())

  // Cursor throttling state
  const lastSentRef = useRef(0)
  const pendingCursorRef = useRef<RemoteCursor['p'] | undefined>(undefined)
  const cursorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let cancelled = false
    setStatus('connecting')
    setMembers([])
    remoteCursorsRef.current.clear()

    const handle = connect({
      projectId,
      onEvent: (ev: ServerEvent) => {
        if (cancelled) return
        if (ev.type === 'presence:list') {
          setMembers(ev.members)
        } else if (ev.type === 'presence:join') {
          setMembers((prev) => [...prev.filter((m) => m.id !== ev.member.id), ev.member])
        } else if (ev.type === 'presence:leave') {
          setMembers((prev) => prev.filter((m) => m.id !== ev.user_id))
          remoteCursorsRef.current.delete(ev.user_id)
          forceCursorTick((n) => n + 1)
        } else if (ev.type === 'cursor:move') {
          // The server attaches the sender's PresenceMember to every cursor
          // event. Adopt it eagerly so a recipient who joined late (and got
          // an empty presence:list) still renders the cursor immediately.
          if (ev.member) {
            setMembers((prev) =>
              prev.some((m) => m.id === ev.member!.id)
                ? prev
                : [...prev, ev.member!],
            )
          }
          remoteCursorsRef.current.set(ev.user_id, {
            userId: ev.user_id,
            p: ev.p,
            t: ev.t,
          })
          forceCursorTick((n) => (n + 1) & 0xffff)
        } else if (ev.type === 'annotation:create' || ev.type === 'annotation:delete') {
          for (const h of annotationHandlersRef.current) {
            try {
              h(ev)
            } catch (err) {
              console.warn('[realtime] handler threw', err)
            }
          }
        } else if (ev.type === 'screenshot:create' || ev.type === 'screenshot:delete') {
          for (const h of screenshotHandlersRef.current) {
            try {
              h(ev)
            } catch (err) {
              console.warn('[realtime] handler threw', err)
            }
          }
        }
      },
      onStatus: (s) => {
        if (!cancelled) setStatus(s)
      },
    })
    handleRef.current = handle

    return () => {
      cancelled = true
      handle.close()
      handleRef.current = null
      if (cursorTimerRef.current != null) {
        clearTimeout(cursorTimerRef.current)
        cursorTimerRef.current = null
      }
    }
  }, [projectId])

  const emitCursor = useCallback((p: RemoteCursor['p']) => {
    pendingCursorRef.current = p
    const now = Date.now()
    const elapsed = now - lastSentRef.current
    if (elapsed >= CURSOR_THROTTLE_MS) {
      lastSentRef.current = now
      handleRef.current?.send({ type: 'cursor:move', p: pendingCursorRef.current ?? null })
      pendingCursorRef.current = undefined
      return
    }
    // Otherwise schedule a flush at the next throttle boundary.
    if (cursorTimerRef.current == null) {
      cursorTimerRef.current = setTimeout(() => {
        cursorTimerRef.current = null
        if (pendingCursorRef.current === undefined) return
        lastSentRef.current = Date.now()
        handleRef.current?.send({ type: 'cursor:move', p: pendingCursorRef.current })
        pendingCursorRef.current = undefined
      }, CURSOR_THROTTLE_MS - elapsed)
    }
  }, [])

  const onAnnotation = useCallback((handler: (ev: AnnotationEvent) => void) => {
    annotationHandlersRef.current.add(handler)
    return () => {
      annotationHandlersRef.current.delete(handler)
    }
  }, [])

  const onScreenshot = useCallback((handler: (ev: ScreenshotEvent) => void) => {
    screenshotHandlersRef.current.add(handler)
    return () => {
      screenshotHandlersRef.current.delete(handler)
    }
  }, [])

  const api = useMemo<RealtimeAPI>(() => ({
    status,
    members,
    remoteCursors: remoteCursorsRef.current,
    emitCursor,
    onAnnotation,
    onScreenshot,
  }), [status, members, emitCursor, onAnnotation, onScreenshot])

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>
}
